import { c as _c } from "react/compiler-runtime";
import { cn } from '@/lib/ui/utils';

/** Normalise a ticker to a `$`-prefixed symbol, or null when absent. */
function toSymbol(ticker: string | null | undefined): string | null {
  if (ticker === null || ticker === undefined || ticker === '') return null;
  return ticker.startsWith('$') ? ticker : `$${ticker}`;
}

/** Standalone monospace ticker chip (e.g. `$PIZZA`). Renders nothing when the
 *  ticker is absent. Shared by the title pairings and the header meta rows.
 *
 *  When `onClick` is provided the chip becomes an interactive button (e.g. the
 *  market header's copy-to-clipboard affordance); the displayed `$`-prefixed
 *  symbol is handed back to the callback. */
export function TickerChip(t0) {
  const $ = _c(20);
  const {
    ticker,
    className,
    onClick,
    "aria-label": ariaLabel
  } = t0;
  let t1;
  if ($[0] !== ticker) {
    t1 = toSymbol(ticker);
    $[0] = ticker;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const symbol = t1;
  if (symbol === null) {
    return null;
  }
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] !== ariaLabel || $[3] !== className || $[4] !== onClick || $[5] !== symbol) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const baseClass = cn("shrink-0 rounded-item bg-background-tertiary px-1.5 pt-[4.5px] pb-[3px] font-mono text-caption font-semibold leading-none text-foreground-secondary", className);
      if (onClick === undefined) {
        t6 = <span className={baseClass}>{symbol}</span>;
        break bb0;
      }
      t2 = "button";
      if ($[11] !== onClick || $[12] !== symbol) {
        t3 = () => onClick(symbol);
        $[11] = onClick;
        $[12] = symbol;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      t4 = ariaLabel;
      t5 = cn(baseClass, "cursor-pointer transition-colors duration-fast ease-default hover:text-foreground-primary");
    }
    $[2] = ariaLabel;
    $[3] = className;
    $[4] = onClick;
    $[5] = symbol;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[14] !== symbol || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t7 = <button type={t2} onClick={t3} aria-label={t4} className={t5}>{symbol}</button>;
    $[14] = symbol;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
interface TickerDisplayProps {
  /** Primary label — the outcome or market name. */
  label: string;
  /** Ticker symbol; the leading `$` is added if absent. Null/empty hides the
   *  chip and renders the label alone. */
  ticker: string | null | undefined;
  /** Allow the label to wrap to multiple lines instead of truncating. Use for
   *  long titles (e.g. the winner-page market recap header). */
  wrap?: boolean;
  labelClassName?: string;
  className?: string;
}

/**
 * A name paired with its monospace ticker chip (e.g. "New Haven  $NHVN").
 * Shared across the resolved panel winner header and the winner detail page.
 */
export function TickerDisplay(t0) {
  const $ = _c(15);
  const {
    label,
    ticker,
    wrap: t1,
    labelClassName,
    className
  } = t0;
  const wrap = t1 === undefined ? false : t1;
  const t2 = wrap && "flex-wrap";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("flex min-w-0 items-baseline gap-1.5", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = wrap ? "min-w-0" : "truncate";
  let t5;
  if ($[3] !== labelClassName || $[4] !== t4) {
    t5 = cn(t4, labelClassName);
    $[3] = labelClassName;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== label || $[7] !== t5) {
    t6 = <span className={t5}>{label}</span>;
    $[6] = label;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== ticker) {
    t7 = <TickerChip ticker={ticker} />;
    $[9] = ticker;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t3 || $[12] !== t6 || $[13] !== t7) {
    t8 = <span className={t3}>{t6}{t7}</span>;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}